import api, { methods } from "@api";
import { toast } from "@provider/toast";
import type { Account } from "@type/account";
import type { Product } from "@type/product";
import type { Recipe } from "@type/recipe";
import { debounce, normalDebounce } from "@utils";
import { create } from "zustand";
import type { ProductType } from "@type/global";
import { StockErrorType } from "@type/stock-errors";

type CartObject = {
  user: Account;
  sales: boolean;
  has_round_order: false | { confirmed: number }[];
  activated_advantage_keys: any[];
  products: BetweenProduct[];
  totals: Totals;
  user_confirmed_quantities?: Record<number, number>; // pid -> quantity mapping
  current_stock_values?: Record<number, number>; // pid -> current voorraad mapping
};

interface ShoppingcartState {
  cart: CartObject | null;
  optimisticUpdates: boolean;
  isRequesting: boolean;
  isLoadingCart: boolean;
  hasError: boolean;
  error: string;
  load: () => void;
  addOrUpdateProduct: (product: ProductType, amount: number, token: string, recipeId?: number) => void;
}

export interface BetweenProduct {
  id?: number;
  round?: number;
  user_id?: number;
  session_id?: any;
  product_id: number;
  pid: number;
  quantity: number;
  advantage_key_id?: any;
  free_shipping_advantage_key_id?: any;
  ip?: string;
  created_at?: string;
  updated_at?: string;
  product: Product;
  recipe_id?: number;
  recipe?: Recipe;
  effective_stock?: number;
  max_quantity?: number;
}

export interface Totals {
  count_products: number;
  count_advantagekeys_cart: number;
  stamps_to_collect: number;
  subtotal: number;
  discount: number;
  delivery_costs: number;
  end_total: number;
  statiegeld: number;
  statiegeld_retour: number;
}

type AddOrUpdateCart = {
  pid: number;
  product_id: number;
  recipe_id?: number;
  quantity: number;
};

const getShoppingCartDebouncer = normalDebounce(() => useShoppingcartStore.getState().load(), 1200);
export const updateCartDebounced = () => getShoppingCartDebouncer();
// normalDebounce(() => {
//   useShoppingcartStore.getState().load(token);
// }, 1200);

function updateDebounced(params: { data: AddOrUpdateCart; alreadyInCart: boolean; oldCart: CartObject }) {
  const { data, alreadyInCart, oldCart } = params;
  debounce(
    async () => {
      try {
        const res = await api<undefined>({
          path: alreadyInCart ? "/shopping-cart/undefined" : "/shopping-cart",
          method: methods.POST,
          body: alreadyInCart ? { data, _method: "patch" } : { ...data },
        });

        return res;
      } catch (err: any) {
        // Handle stock validation errors
        if (err.type === StockErrorType.OUT_OF_STOCK || err.type === StockErrorType.INSUFFICIENT_STOCK) {
          toast.show(err.message || 'Dit product heeft een beperkte voorraad');
          
          // If backend returns available quantity, update cart to that amount
          if (err.quantity !== undefined) {
            const updatedCart = { ...oldCart };
            const productIndex = updatedCart.products.findIndex(
              p => p.product_id === data.product_id && p.recipe_id === data.recipe_id
            );
            if (productIndex >= 0 && err.quantity > 0) {
              updatedCart.products[productIndex]!.quantity = err.quantity;
              useShoppingcartStore.setState((prev) => ({ ...prev, cart: updatedCart }));
            } else {
              // Revert to old cart if quantity is 0 or product not found
              useShoppingcartStore.setState((prev) => ({ ...prev, cart: oldCart }));
            }
          } else {
            // Revert to old cart
            useShoppingcartStore.setState((prev) => ({ ...prev, cart: oldCart }));
          }
        } else {
          // Other errors
          toast.show(err.message || 'Er is een fout opgetreden');
          useShoppingcartStore.setState((prev) => ({ ...prev, cart: oldCart }));
        }
      }
    },
    `product_${data.product_id}`,
    400
  );
}

export const useShoppingcartStore = create<ShoppingcartState>((set, get) => ({
  cart: null,
  optimisticUpdates: false,
  isRequesting: false,
  isLoadingCart: false,
  hasError: false,
  error: "",
  load: async () => {
    set({ isRequesting: true, isLoadingCart: !get().cart, hasError: false, error: "" });
    try {
      const response = await api<CartObject>({
        path: "/shopping-cart",
        method: methods.GET,
      });

      const responseWithAdvantages = enhanceCartObjectWithFreeProductAdvantages(response);

      if (get().isRequesting) {
        set({ cart: responseWithAdvantages, optimisticUpdates: false, isRequesting: false, isLoadingCart: false });
      }
    } catch (e) {
      console.log("Error loading cart:", e);
      toast.show("Fout bij laden winkelmandje");
      set({ isRequesting: false, isLoadingCart: false, hasError: true, error: (e as any).message });
    }
  },
  addOrUpdateProduct: async (product, amount, token, recipeId) => {
    set({ isRequesting: false });
    const oldCart = get().cart;
    if (oldCart === null) {
      console.log("CART IS NULL");
      return;
    }

    const newProducts = [...oldCart.products];
    let existingProductIndex = newProducts.findIndex((prod) => prod.product_id === product.id);
    if (recipeId) {
      existingProductIndex = newProducts.findIndex(
        (prod) => prod.product_id === product.id && prod.recipe_id === recipeId
      );
    }

    const alreadyInCart = existingProductIndex >= 0;

    if (alreadyInCart) {
      if (amount === 0) {
        newProducts.splice(existingProductIndex, 1);
      } else {
        newProducts[existingProductIndex] = {
          ...newProducts[existingProductIndex]!,
          quantity: amount,
          recipe_id: recipeId,
        };
      }
    } else {
      const obj: BetweenProduct = {
        product,
        pid: product.pid,
        quantity: amount,
        product_id: product.id,
      };

      if (recipeId) {
        obj.recipe_id = recipeId;
      }
      newProducts.push(obj);
    }

    const newCart = { ...oldCart, products: newProducts };
    set({ cart: newCart, optimisticUpdates: true });

    const bodyData = { pid: product.pid, quantity: amount, product_id: product.id };

    if (recipeId) {
      (bodyData as any).recipe_id = recipeId;
    }
    updateDebounced({
      data: bodyData,
      alreadyInCart,
      oldCart,
    });
    updateCartDebounced();
  },

  removeAllProducts: () =>
    set(({ cart }) => {
      return { cart: { ...cart!, products: [] } };
    }),
  reset: () => set({ cart: null, optimisticUpdates: false, isRequesting: false, isLoadingCart: false }),
}));

export function useCartAmount(productId: number, recipeId?: number) {
  return useShoppingcartStore((store) => {
    if (recipeId) {
      return store.cart?.products.find((p) => p.product_id === productId && p.recipe_id === recipeId)?.quantity;
    }
    return store.cart?.products.find((p) => p.product_id === productId && !p.recipe_id)?.quantity;
  });
}

export function useCartBadge() {
  return useShoppingcartStore((store) => store.cart?.products.reduce((a, b) => a + b.quantity, 0)) || 0;
}

function enhanceCartObjectWithFreeProductAdvantages(response: CartObject): CartObject {
  // Copied from Old VUE WebApp
  if (response.activated_advantage_keys.length && response.totals.subtotal > 30) {
    const hasOneProductFreeAdvantageKey = response.activated_advantage_keys.find(
      (a) => a.advantage_key_type.type === "one_product_free"
    );
    const hasFreeProductAdvantageKey = response.activated_advantage_keys.find(
      (a) => a.advantage_key_type.type === "free_product"
    );

    if (hasOneProductFreeAdvantageKey) {
      const hasProductInShoppingCartIndex = response.products.findIndex(
        (p) => p.pid === hasOneProductFreeAdvantageKey.pid
      );
      if (hasProductInShoppingCartIndex >= 0) {
        response.products[hasProductInShoppingCartIndex]!.advantage_key_id = hasOneProductFreeAdvantageKey;
      }

      if (hasFreeProductAdvantageKey) {
        response.products.push({
          ...hasFreeProductAdvantageKey,
          quantity: 1,
          advantage_key_id: hasFreeProductAdvantageKey,
        });
      }
    }
  }
  return response;
}
